/**
 * 系统消息模拟数据
 *
 * @description 提供系统消息功能开发和测试用的模拟数据
 * - 包含有奖励和无奖励的消息
 * - 包含不同时间的消息（今天、昨天、更早）
 * - 包含不同长度的消息内容
 * - 只使用 gold 类型的奖励
 *
 * @date 2025-09-16
 */

import type { SystemMessage, ItemInfo } from '@/api/message/types'
import { formatUtcToLocal, smartDateFormat } from '@/utils/date'

/**
 * 创建奖励数据（items + iteminfos）
 * @param rewards 奖励配置数组，每个元素包含 {id, amount}
 * @returns {items: any[], iteminfos: ItemInfo[]}
 */
const createRewardData = (rewards: Array<{ id: string; amount: number }>): { items: any[]; iteminfos: ItemInfo[] } => {
    const items = rewards.map((reward) => ({
        id: reward.id,
        num: reward.amount.toString(),
    }))

    const iteminfos = rewards.map((reward) => ({
        id: reward.id,
        name: reward.id, // 使用ID作为名称，符合真实数据
        img: 'https://resource.betfugu.com/compensationitemsicon/E8mrPW2AnS.png',
        desc: reward.id, // 使用ID作为描述，符合真实数据
        fakeDataMin: reward.amount,
        fakeDataMax: reward.amount,
        realDataMin: reward.amount,
        realDataMax: reward.amount,
        useFor: 'gold',
        flowMultiple: 1,
        updateTime: generateISOTime('now'),
        createTime: generateISOTime('now'),
    }))

    return { items, iteminfos }
}

/**
 * 创建单个金币奖励（兼容旧接口）
 * @param amount 奖励数量
 * @returns 奖励数据对象
 */
const createSingleGoldReward = (amount: number) => {
    return createRewardData([{ id: '11', amount }])
}

/**
 * 创建多个奖励
 * @param rewards 奖励配置数组
 * @returns 奖励数据对象
 */
const createMultipleRewards = (rewards: Array<{ id: string; amount: number }>) => {
    return createRewardData(rewards)
}

/**
 * 生成模拟时间（返回时间戳，用于系统消息的time字段）
 */
const generateTime = (type: 'now' | 'hour' | 'today' | 'yesterday' | 'week' | 'month'): number => {
    const now = Date.now()
    switch (type) {
        case 'now':
            return now - Math.random() * 5 * 60 * 1000 // 5分钟内
        case 'hour':
            return now - Math.random() * 60 * 60 * 1000 // 1小时内
        case 'today':
            return now - Math.random() * 12 * 60 * 60 * 1000 // 12小时内
        case 'yesterday':
            return now - 24 * 60 * 60 * 1000 - Math.random() * 12 * 60 * 60 * 1000 // 昨天
        case 'week':
            return now - Math.random() * 7 * 24 * 60 * 60 * 1000 // 一周内
        case 'month':
            return now - Math.random() * 30 * 24 * 60 * 60 * 1000 // 一个月内
        default:
            return now
    }
}

/**
 * 生成系统返回的ISO时间字符串格式（如 "2025-09-09T15:59:20.438Z"）
 */
const generateISOTime = (type: 'now' | 'hour' | 'today' | 'yesterday' | 'week' | 'month'): string => {
    const timestamp = generateTime(type)
    return new Date(timestamp).toISOString()
}

/**
 * 系统消息模拟数据
 */
export const mockSystemMessages: SystemMessage[] = [
    // 1. 未读 + 有奖励 + 最新消息 (需要弹窗)
    {
        channel: 'system',
        id: 1001,
        isread: false,
        ispop: false, // 🔧 改为false表示需要弹窗
        event: null,
        time: generateTime('now'),
        content: {
            context: {
                id: 'daily-bonus-001',
                title: 'Daily Login Bonus',
                text: 'Congratulations! You have successfully claimed your daily login bonus. Thank you for being a loyal player!',
                ...createSingleGoldReward(500),
                currency: 'USD',
            },
            text: 'Daily login bonus received',
            sender: 'system',
            receiver: 'user',
        },
    },

    // 2. 未读 + 无奖励 + 重要通知 (需要弹窗)
    {
        channel: 'system',
        id: 1002,
        isread: false,
        ispop: false, // 🔧 改为false表示需要弹窗
        event: null,
        time: generateTime('hour'),
        content: {
            context: {
                id: 'maintenance-notice-001',
                title: 'Scheduled Maintenance Notice',
                text: 'Dear Player,<br><br>We will be performing scheduled maintenance on our servers from 2:00 AM to 4:00 AM (UTC) tomorrow. During this time, the game will be temporarily unavailable.<br><br>We apologize for any inconvenience this may cause and appreciate your understanding.',
                items: [],
                currency: 'USD',
                iteminfos: [], // 无奖励
            },
            text: 'Server maintenance notification',
            sender: 'system',
            receiver: 'user',
        },
    },

    // 3. 已读 + 有奖励 + VIP奖励
    {
        channel: 'system',
        id: 1003,
        isread: true,
        ispop: true,
        event: null,
        time: generateTime('today'),
        content: {
            context: {
                id: 'vip-monthly-001',
                title: 'Monthly VIP Reward',
                text: 'Congratulations! Your monthly VIP reward is now available. As a VIP member, you receive exclusive benefits and bonuses.',
                ...createSingleGoldReward(2000),
                currency: 'USD',
            },
            text: 'Monthly VIP reward claimed',
            sender: 'system',
            receiver: 'user',
        },
    },

    // 4. 未读 + 有奖励 + 活动奖励（多个奖励示例）
    {
        channel: 'system',
        id: 1004,
        isread: false,
        ispop: false,
        event: null,
        time: generateTime('today'),
        content: {
            context: {
                id: 'event-reward-001',
                title: 'Special Event Reward',
                text: 'Congratulations! You have completed the special weekend event. Here is your reward for participating.',
                ...createMultipleRewards([
                    { id: '11', amount: 3 },
                    { id: '12', amount: 2 },
                    { id: '13', amount: 1 },
                ]),
                currency: 'USD',
            },
            text: 'Weekend event reward',
            sender: 'system',
            receiver: 'user',
        },
    },

    // 5. 已读 + 无奖励 + 系统公告
    {
        channel: 'system',
        id: 1005,
        isread: true,
        ispop: true,
        event: null,
        time: generateTime('yesterday'),
        content: {
            context: {
                id: 'announcement-001',
                title: 'New Features Available',
                text: 'We are excited to announce that new features have been added to the game! Check out the latest updates in the game lobby.',
                items: [],
                currency: 'USD',
                iteminfos: [], // 无奖励
            },
            text: 'New features announcement',
            sender: 'system',
            receiver: 'user',
        },
    },

    // 6. 未读 + 无奖励 + 长文本消息
    {
        channel: 'system',
        id: 1006,
        isread: false,
        ispop: true,
        event: null,
        time: generateTime('yesterday'),
        content: {
            context: {
                id: 'terms-update-001',
                title: 'Terms of Service Update',
                text: 'Dear Valued Player,<br><br>We wanted to inform you about important updates to our Terms of Service. These changes take effect on October 1st, 2024.<br><br>Key updates include:<br>• Enhanced security measures<br>• Updated privacy policy<br>• Revised bonus terms and conditions<br>• Improved dispute resolution process<br><br>Please take a moment to review these changes in the settings menu. If you have any questions, our customer support team is here to help.<br><br>Thank you for your continued trust and support.',
                items: [],
                currency: 'USD',
                iteminfos: [], // 无奖励
            },
            text: 'Terms of service update notification',
            sender: 'system',
            receiver: 'user',
        },
    },

    // 7. 已读 + 有奖励 + 充值奖励
    {
        channel: 'system',
        id: 1007,
        isread: true,
        ispop: true,
        event: null,
        time: generateTime('week'),
        content: {
            context: {
                id: 'deposit-bonus-001',
                title: 'Deposit Bonus Credited',
                text: 'Thank you for your deposit! Your bonus has been successfully credited to your account.',
                ...createSingleGoldReward(750),
                currency: 'USD',
            },
            text: 'Deposit bonus received',
            sender: 'system',
            receiver: 'user',
        },
    },

    // 8. 未读 + 有奖励 + 推荐奖励
    {
        channel: 'system',
        id: 1008,
        isread: false,
        ispop: true,
        event: null,
        time: generateTime('week'),
        content: {
            context: {
                id: 'referral-reward-001',
                title: 'Friend Referral Reward',
                text: 'Great news! Your friend has completed their first deposit using your referral code. Here is your referral bonus!',
                ...createSingleGoldReward(300),
                currency: 'USD',
            },
            text: 'Referral bonus earned',
            sender: 'system',
            receiver: 'user',
        },
    },

    // 9. 已读 + 无奖励 + 安全提醒
    {
        channel: 'system',
        id: 1009,
        isread: true,
        ispop: true,
        event: null,
        time: generateTime('week'),
        content: {
            context: {
                id: 'security-reminder-001',
                title: 'Account Security Reminder',
                text: 'For your account security, please remember to:<br>• Never share your login credentials<br>• Use a strong, unique password<br>• Enable two-factor authentication<br>• Log out from shared devices',
                items: [],
                currency: 'USD',
                iteminfos: [], // 无奖励
            },
            text: 'Security reminder notice',
            sender: 'system',
            receiver: 'user',
        },
    },

    // 10. 未读 + 无奖励 + 简短消息
    {
        channel: 'system',
        id: 1010,
        isread: false,
        ispop: true,
        event: null,
        time: generateTime('month'),
        content: {
            context: {
                id: 'welcome-001',
                title: 'Welcome!',
                text: 'Welcome to our platform! We hope you enjoy your gaming experience.',
                items: [],
                currency: 'USD',
                iteminfos: [], // 无奖励
            },
            text: 'Welcome message',
            sender: 'system',
            receiver: 'user',
        },
    },

    // 11. 已读 + 有奖励 + 周任务完成
    {
        channel: 'system',
        id: 1011,
        isread: true,
        ispop: true,
        event: null,
        time: generateTime('month'),
        content: {
            context: {
                id: 'weekly-task-001',
                title: 'Weekly Task Completed',
                text: 'Excellent work! You have completed all weekly tasks. Your dedication has been rewarded!',
                ...createSingleGoldReward(1500),
                currency: 'USD',
            },
            text: 'Weekly task completion reward',
            sender: 'system',
            receiver: 'user',
        },
    },

    // 12. 未读 + 无奖励 + 客服消息
    {
        channel: 'system',
        id: 1012,
        isread: false,
        ispop: true,
        event: null,
        time: generateTime('month'),
        content: {
            context: {
                id: 'support-response-001',
                title: 'Customer Support Response',
                text: "Thank you for contacting our customer support team. Your inquiry has been resolved. If you need further assistance, please don't hesitate to reach out.",
                items: [],
                currency: 'USD',
                iteminfos: [], // 无奖励
            },
            text: 'Customer support response',
            sender: 'system',
            receiver: 'user',
        },
    },
]

/**
 * 获取模拟的系统消息列表
 * @param limit 限制返回的消息数量，默认返回所有
 * @returns 系统消息数组
 */
export const getMockSystemMessages = (limit?: number): SystemMessage[] => {
    const messages = [...mockSystemMessages].sort((a, b) => b.time - a.time) // 按时间倒序排列（最新的在前）
    return limit ? messages.slice(0, limit) : messages
}

/**
 * 获取未读消息数量
 * @returns 未读消息数量
 */
export const getMockUnreadCount = (): number => {
    return mockSystemMessages.filter((msg) => !msg.isread).length
}

/**
 * 根据ID查找消息
 * @param id 消息ID
 * @returns 系统消息或undefined
 */
export const getMockMessageById = (id: number): SystemMessage | undefined => {
    return mockSystemMessages.find((msg) => msg.id === id)
}

/**
 * 获取需要弹窗显示的消息
 * @returns 需要弹窗的未读消息数组
 */
export const getMockPopupMessages = (): SystemMessage[] => {
    return mockSystemMessages.filter((msg) => msg.ispop && !msg.isread)
}

/**
 * 模拟标记消息为已读
 * @param id 消息ID
 * @returns 操作是否成功
 */
export const mockMarkAsRead = (id: number): boolean => {
    const message = mockSystemMessages.find((msg) => msg.id === id)
    if (message) {
        message.isread = true
        console.log(`🎭 模拟：消息 ${id} 已标记为已读`)
        return true
    }
    return false
}

/**
 * 模拟批量标记消息为已读
 * @param ids 消息ID数组
 * @returns 成功标记的消息数量
 */
export const mockBatchMarkAsRead = (ids: number[]): number => {
    let successCount = 0
    ids.forEach((id) => {
        if (mockMarkAsRead(id)) {
            successCount++
        }
    })
    console.log(`🎭 模拟：批量标记 ${successCount}/${ids.length} 条消息为已读`)
    return successCount
}

/**
 * 模拟标记消息为已弹框
 * @param id 消息ID
 * @returns 操作是否成功
 */
export const mockMarkAsPopped = (id: number): boolean => {
    const message = mockSystemMessages.find((msg) => msg.id === id)
    if (message) {
        message.ispop = true
        console.log(`🎭 模拟：消息 ${id} 已标记为已弹框`)
        return true
    }
    return false
}

// 全局计数器，用于生成动态内容
let messageCounter = 1

/**
 * 生成动态消息内容
 * @param type 消息类型：'reward' | 'notice' | 'announcement'
 * @param counter 消息计数
 * @returns 消息内容对象
 */
const generateDynamicContent = (type: 'reward' | 'notice' | 'announcement', counter: number) => {
    const contents = {
        reward: {
            title: `New Reward #${counter}`,
            text: `Congratulations! You have received a new reward #${counter}. This is a dynamically generated reward message to test real-time updates.`,
            hasReward: true,
        },
        notice: {
            title: `System Notice #${counter}`,
            text: `Important Notice #${counter}: This is a system notification generated dynamically for testing purposes. Please check the latest updates.`,
            hasReward: false,
        },
        announcement: {
            title: `Announcement #${counter}`,
            text: `New Announcement #${counter}: We have exciting news to share! This dynamic announcement is generated for testing real-time message updates.`,
            hasReward: false,
        },
    }

    const content = contents[type]
    const baseContext = {
        id: `dynamic-${type}-${counter}`,
        title: content.title,
        text: content.text,
        currency: 'USD',
    }

    if (content.hasReward) {
        return {
            context: {
                ...baseContext,
                ...createSingleGoldReward(Math.floor(Math.random() * 1000) + 100), // 随机奖励 100-1100
            },
            text: `Dynamic ${type} message #${counter}`,
        }
    } else {
        return {
            context: {
                ...baseContext,
                items: [],
                iteminfos: [],
            },
            text: `Dynamic ${type} message #${counter}`,
        }
    }
}

/**
 * 模拟添加新消息（用于测试实时推送）
 * @param options 可选配置参数
 * @param options.type 消息类型，默认为 'notice'
 * @param options.isRead 是否已读，默认随机分配
 * @param options.needPopup 是否需要弹窗，默认随机分配
 * @returns 新创建的消息对象
 */
export const mockAddNewMessage = (
    options: {
        type?: 'reward' | 'notice' | 'announcement'
        isRead?: boolean
        needPopup?: boolean
    } = {}
): SystemMessage => {
    const {
        type = 'notice',
        isRead = Math.random() < 0.3, // 30% 概率已读
        needPopup = Math.random() < 0.4, // 40% 概率需要弹窗
    } = options

    const dynamicContent = generateDynamicContent(type, messageCounter)

    const newMessage: SystemMessage = {
        channel: 'system',
        id: Math.max(...mockSystemMessages.map((m) => m.id)) + 1,
        isread: isRead,
        ispop: needPopup,
        event: null,
        time: Date.now(), // 🔧 保持时间最新
        content: {
            ...dynamicContent,
            sender: 'system',
            receiver: 'user',
        },
    }

    mockSystemMessages.unshift(newMessage) // 添加到开头
    messageCounter++ // 递增计数器

    console.log(`🎭 模拟：新消息已添加 #${messageCounter - 1}`, {
        id: newMessage.id,
        title: newMessage.content.context.title,
        isRead: newMessage.isread,
        needPopup: newMessage.ispop,
        type,
    })

    return newMessage
}

/**
 * 将ISO时间字符串转换为本地时间格式
 * @param isoString ISO时间字符串（如 "2025-09-09T15:59:20.438Z"）
 * @returns 本地时间格式字符串
 */
export const formatISOToLocal = (isoString: string): string => {
    return formatUtcToLocal(isoString)
}

/**
 * 快速生成奖励类型的新消息
 * @param isRead 是否已读，默认为未读
 * @returns 新的奖励消息
 */
export const mockAddRewardMessage = (isRead = false): SystemMessage => {
    return mockAddNewMessage({ type: 'reward', isRead, needPopup: !isRead })
}

/**
 * 快速生成通知类型的新消息
 * @param isRead 是否已读，默认为未读
 * @returns 新的通知消息
 */
export const mockAddNoticeMessage = (isRead = false): SystemMessage => {
    return mockAddNewMessage({ type: 'notice', isRead, needPopup: false })
}

/**
 * 快速生成公告类型的新消息
 * @param isRead 是否已读，默认为未读
 * @returns 新的公告消息
 */
export const mockAddAnnouncementMessage = (isRead = false): SystemMessage => {
    return mockAddNewMessage({ type: 'announcement', isRead, needPopup: true })
}

/**
 * 重置消息计数器（用于测试时重置）
 */
export const resetMessageCounter = (): void => {
    messageCounter = 1
    console.log('🎭 模拟：消息计数器已重置')
}

/**
 * 获取当前消息计数器值
 */
export const getMessageCounter = (): number => {
    return messageCounter
}

/**
 * 模拟系统消息推送（用于测试 useMonitorMsg.ts 中的 handleSystemMsg）
 * @param options 推送配置选项
 * @param options.type 消息类型，默认为 'reward'
 * @param options.isRead 是否已读，默认为 false（新推送的消息通常是未读的）
 * @param options.needPopup 是否需要弹窗，默认为 true（推送消息通常需要弹窗）
 * @param options.delay 延迟推送时间（毫秒），默认为 1000ms
 * @returns Promise<SystemMessage> 推送的消息对象
 */
export const mockPushSystemMessage = async (
    options: {
        type?: 'reward' | 'notice' | 'announcement'
        isRead?: boolean
        needPopup?: boolean
        delay?: number
    } = {}
): Promise<SystemMessage> => {
    const {
        type = 'reward',
        isRead = false, // 新推送的消息默认未读
        needPopup = true, // 新推送的消息默认需要弹窗
        delay = 1000, // 默认延迟1秒，模拟网络传输
    } = options

    console.log(`🚀 模拟系统消息推送开始，${delay}ms 后触发...`, {
        type,
        isRead,
        needPopup,
    })

    // 模拟网络延迟
    await new Promise((resolve) => setTimeout(resolve, delay))

    // 创建新消息
    const newMessage = mockAddNewMessage({ type, isRead, needPopup })

    console.log('📡 模拟推送：准备触发 handleSystemMsg...', {
        messageId: newMessage.id,
        title: newMessage.content.context.title,
        hasReward: !!(newMessage.content.context.iteminfos && newMessage.content.context.iteminfos.length > 0),
    })

    // 🔥 关键：模拟触发 useMonitorMsg.ts 中的 handleSystemMsg
    // 通过导入 stores 和 bus 来模拟真实的推送过程
    try {
        // 动态导入避免循环依赖
        const { useBaseStore } = await import('@/stores')
        const bus = await import('@/utils/bus')

        const store = useBaseStore()

        // 模拟 pomelo2 推送的消息格式
        const pushMessage = {
            id: newMessage.id,
            content: newMessage.content,
            ispop: newMessage.ispop,
            isread: newMessage.isread,
            time: newMessage.time,
            channel: newMessage.channel,
            event: newMessage.event,
        }

        console.log('🎯 模拟推送：触发 handleSystemMsg', pushMessage)

        // 直接调用 handleSystemMsg 的逻辑（复制 useMonitorMsg.ts 中的逻辑）
        // 1. 添加新消息到 baseStore
        store.addSystemMessage(pushMessage)
        console.log('✅ 模拟推送：新系统消息已添加到store:', pushMessage.id)

        // 2. 检查消息是否包含奖励道具
        const hasReward = pushMessage.content?.context?.iteminfos?.length > 0
        if (hasReward) {
            console.log('🎁 模拟推送：收到包含奖励的系统消息，触发礼品数据更新')
            // 更新礼品数据
            bus.default.emit('freeGametimes')
        }

        // 3. 如果是需要弹窗的消息，触发弹框检查
        if (!pushMessage.ispop) {
            console.log('🎉 模拟推送：收到需要弹窗的系统消息，通知弹框系统检查')
            // 📝 简化方案：只需要触发一次队列检查
            // bus.default.emit('checkPopupQueue')
        }

        console.log('🎊 模拟推送完成！系统消息已成功推送并触发相关逻辑')
    } catch (error) {
        console.error('❌ 模拟推送失败:', error)
        throw error
    }

    return newMessage
}

export default {
    getMockSystemMessages,
    getMockUnreadCount,
    getMockMessageById,
    getMockPopupMessages,
    mockMarkAsRead,
    mockBatchMarkAsRead,
    mockMarkAsPopped,
    mockAddNewMessage,
    mockAddRewardMessage,
    mockAddNoticeMessage,
    mockAddAnnouncementMessage,
    mockPushSystemMessage,
    resetMessageCounter,
    getMessageCounter,
    formatISOToLocal,
}
